// Global auth state (replace localStorage)

import { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import authService from '../api/services/auth.service';
import tokenManager from '../api/utils/tokenManager';

const AuthContext = createContext();

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [role, setRole] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const syncFromStorage = () => {
    try {
      const isAuth = tokenManager.isAuthenticated();
      const storedUser = tokenManager.getUserData();
      const decodedUser = tokenManager.getUserFromToken();
      const userData = storedUser || decodedUser || null;
      const userRoleRaw = tokenManager.getRole() || tokenManager.getUserRole() || null;
      const userRole = typeof userRoleRaw === 'string' ? userRoleRaw.toLowerCase() : userRoleRaw;

      if (isAuth && userData) {
        setUser(userData);
        setRole(userRole);
        setIsAuthenticated(true);
      } else {
        setUser(null);
        setRole(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('Error initializing auth:', error);
      setUser(null);
      setRole(null);
      setIsAuthenticated(false);
    }
  };

  useEffect(() => {
    syncFromStorage();
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleRoleUpdate = () => {
      syncFromStorage();
    };

    window.addEventListener('roleUpdated', handleRoleUpdate);
    return () => window.removeEventListener('roleUpdated', handleRoleUpdate);
  }, []);

  const login = async (credentials) => {
    try {
      // Don't reset token here, authService will handle it correctly
      const result = await authService.login(credentials);
      
      const userRoleRaw = result.user.role;
      const userRole = typeof userRoleRaw === 'string' ? userRoleRaw.toLowerCase() : userRoleRaw;
      
      setUser(result.user);
      setRole(userRole);
      setIsAuthenticated(true);
      
      return result;
    } catch (error) {
      throw error;
    }
  };

  const register = async (userData) => {
    try {
      // User must manually login after registration, so we don't update state here.
      const result = await authService.register(userData);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    setRole(null);
    setIsAuthenticated(false);
  };

  const forgotPassword = async (email) => {
    try {
      const result = await authService.forgotPassword(email);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const verifyOtp = async (email, otp) => {
    try {
      const result = await authService.verifyOtp(email, otp);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const resetPassword = async (email, newPassword) => {
    try {
      const result = await authService.resetPassword(email, newPassword);
      return result;
    } catch (error) {
      throw error;
    }
  };

  const isAdmin = () => {
    return role === 'admin';
  };

  const isUser = () => {
    return role === 'user';
  };

  const value = {
    user,
    role,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    forgotPassword,
    verifyOtp,
    resetPassword,
    isAdmin,
    isUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useCurrentAuthSpace = () => {
  const path = window.location?.pathname || '';
  if (path.startsWith('/admin')) return 'admin';
  return 'user';
};

export default AuthContext;

